/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Pinned endpoint namespace
 */
export const EndpointNamespaceKind = {
  Platform: "platform",
  CustomDomain: "custom_domain",
} as const;
/**
 * Pinned endpoint namespace
 */
export type EndpointNamespaceKind = OpenEnum<typeof EndpointNamespaceKind>;

/**
 * Private-network provider
 */
export const NetworkIngressProvider = {
  Tailscale: "tailscale",
} as const;
/**
 * Private-network provider
 */
export type NetworkIngressProvider = OpenEnum<typeof NetworkIngressProvider>;

export type NetworkIngress = {
  connectedSince?: Date | undefined;
  createdAt: Date;
  /**
   * Whether provider credentials are stored; credential values are never returned
   */
  credentialsConfigured: boolean;
  /**
   * Pinned custom-domain ID when endpoint_namespace_kind is custom_domain
   */
  customDomainId?: string | undefined;
  /**
   * Observed private DNS name
   */
  dnsName?: string | undefined;
  /**
   * Whether desired-state reconciliation may keep the ingress online
   */
  enabled: boolean;
  /**
   * Pinned endpoint namespace
   */
  endpointNamespaceKind: EndpointNamespaceKind;
  healthCheckedAt?: Date | undefined;
  /**
   * Private DNS label advertised by the provider
   */
  hostname: string;
  /**
   * Network ingress ID
   */
  id: string;
  /**
   * Whether an attributable provider identity is required
   */
  identityRequired: boolean;
  /**
   * Latest redacted error code
   */
  lastError?: string | undefined;
  /**
   * Owning organization ID
   */
  organizationId: string;
  /**
   * Private-network provider
   */
  provider: NetworkIngressProvider;
  /**
   * Latest redacted lifecycle status
   */
  status: string;
  updatedAt: Date;
};

/** @internal */
export const EndpointNamespaceKind$inboundSchema: z.ZodMiniType<
  EndpointNamespaceKind,
  unknown
> = openEnums.inboundSchema(EndpointNamespaceKind);

/** @internal */
export const NetworkIngressProvider$inboundSchema: z.ZodMiniType<
  NetworkIngressProvider,
  unknown
> = openEnums.inboundSchema(NetworkIngressProvider);

/** @internal */
export const NetworkIngress$inboundSchema: z.ZodMiniType<
  NetworkIngress,
  unknown
> = z.pipe(
  z.object({
    connected_since: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    credentials_configured: z.boolean(),
    custom_domain_id: z.optional(z.string()),
    dns_name: z.optional(z.string()),
    enabled: z.boolean(),
    endpoint_namespace_kind: EndpointNamespaceKind$inboundSchema,
    health_checked_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    hostname: z.string(),
    id: z.string(),
    identity_required: z.boolean(),
    last_error: z.optional(z.string()),
    organization_id: z.string(),
    provider: NetworkIngressProvider$inboundSchema,
    status: z.string(),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "connected_since": "connectedSince",
      "created_at": "createdAt",
      "credentials_configured": "credentialsConfigured",
      "custom_domain_id": "customDomainId",
      "dns_name": "dnsName",
      "endpoint_namespace_kind": "endpointNamespaceKind",
      "health_checked_at": "healthCheckedAt",
      "identity_required": "identityRequired",
      "last_error": "lastError",
      "organization_id": "organizationId",
      "updated_at": "updatedAt",
    });
  }),
);

export function networkIngressFromJSON(
  jsonString: string,
): SafeParseResult<NetworkIngress, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => NetworkIngress$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'NetworkIngress' from JSON`,
  );
}
